import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { Modal } from 'bootstrap';
import { CreateUser, UserDto } from '../../_core/_model/users';
import { ApiMeta } from '../../_core/_model/api-response';
import { FormatService } from '../../_core/_services/format';
import { NotificationService } from '../../_core/_services/notification.service';
import { ApiService } from '../../_core/_services/api.service';
import { WalletDto } from '../../_core/_model/wallet';
import { OrderDetailsDto, OrderDto } from '../../_core/_model/order';
import { AuthApiService } from '../../_core/_services/auth-api.service';
import { AuthService } from '../../_core/_services/auth.service';
import { UserRole } from '../../_core/_model/user-role';
import { CountryDto, MiniCountryDto } from '../../_core/_model/countries';
import { Router } from '@angular/router';

@Component({
  selector: 'app-users',
  standalone: false,
  templateUrl: './users.html',
  styleUrl: './users.css',
})
export class Users implements OnInit {
  countries: MiniCountryDto[] = [];
  countriesLoading = false;
  countriesError = '';
  currentRole: UserRole | null = null;

  meta: ApiMeta | null = null;
  metaForOrder: ApiMeta | null = null;
  metaForWallet: ApiMeta | null = null;
  loading = false;
  disable2FaLoading: boolean = false;

  admins: UserDto[] = []; // replace with your DTO
  selectedUser?: UserDto;
  selectedUserWallet?: WalletDto[] = [];
  selectedUserOrders?: OrderDto[] = [];


  userPages: number = 1;
  userOrderPages: number = 1;
  userWalletPages: number = 1;

  private editModal?: Modal;
  private tfaModal?: Modal;
  private userModal?: Modal;

  adminForm: FormGroup;
  editAdminForm: FormGroup;

  deletingId: string | null = null;

  url: any;

  constructor(
    private api: ApiService,
    private authApi: AuthApiService,
    private auth: AuthService,
    private fb: FormBuilder,
    private format: FormatService,
    private notifyService: NotificationService,
    private router: Router,
  ) {
      this.adminForm = this.fb.group({
        firstName: ['', Validators.required],
        lastName: ['', Validators.required],
        email: ['', [Validators.required, Validators.email]],
        phone: [''],
        country: ['', Validators.required],
        role: ['Admin', Validators.required]
      });

      this.editAdminForm = this.fb.group({
        id: [''],
        firstName: ['', Validators.required],
        lastName: ['', Validators.required],
        phone: [''],
        country: ['', Validators.required],
        status: ['Active']
      });

      this.url = this.router.url.slice(1);
  }
  
  get firstName() { return this.adminForm.get('firstName'); }
  get lastName() { return this.adminForm.get('lastName'); }
  get email() { return this.adminForm.get('email'); }
  get phone() { return this.adminForm.get('phone'); }
  get country() { return this.adminForm.get('country'); }
  get role() { return this.adminForm.get('role'); }

  get selectedCountry(): MiniCountryDto | null {
    const code = String(this.country?.value ?? '').trim().toUpperCase();
    return this.countries.find(country => country.code === code) ?? null;
  }

  get selectedCountryFlag(): string {
    return this.countryFlag(this.selectedCountry?.code ?? '');
  }

  get selectedDialCode(): string {
    return this.selectedCountry?.dialCode?.trim() ?? '';
  }

  get phonePlaceholder(): string {
    const country = this.selectedCountry;

    if (country?.phoneNumberPlaceholder?.trim()) {
      return country.phoneNumberPlaceholder.trim();
    }

    return country?.dialCode
      ? '555 123 4567'
      : '+1 555 123 4567';
  }

  get phoneEntrySupported(): boolean {
    const country = this.selectedCountry;

    return !!(
      country?.dialCode &&
      country.phoneNumberMinLength &&
      country.phoneNumberMaxLength &&
      country.phoneNumberFormat
    );
  }

  get phoneInvalid(): boolean {
    const phone = String(this.phone?.value ?? '').trim();

    if (!phone) return false;

    const country = this.selectedCountry;
    if (!country) return true;

    if (this.phoneEntrySupported) {
      const digits = this.extractNationalDigits(phone, country);
      const minimum = country.phoneNumberMinLength ?? 0;
      const maximum = country.phoneNumberMaxLength ?? 0;

      return digits.length < minimum || digits.length > maximum;
    }

    return !this.isValidGenericPhone(phone, country);
  }

  get phoneInternationalPreview(): string {
    return this.buildInternationalPhone(String(this.phone?.value ?? '')) ?? '';
  }

  get staffRoleOptions(): Array<{ value: UserRole; label: string }> {
    const roles: Array<{ value: UserRole; label: string }> = [
      { value: 'Admin', label: 'Administrator' },
      { value: 'Compliance', label: 'Compliance' },
      { value: 'Finance', label: 'Finance' },
      { value: 'Support', label: 'Support' }
    ];

    if (this.currentRole === 'SuperAdmin') {
      roles.unshift({ value: 'SuperAdmin', label: 'Super Administrator' });
    }

    return roles;
  }

  isInvalid(form: FormGroup, name: string): boolean {
    const c = form.get(name);
    return !!c && c.invalid && (c.dirty || c.touched);
  }

  ngOnInit(): void {
    this.auth.getCurrentUser().subscribe(user => {
      this.currentRole = (user?.role as UserRole | undefined) ?? null;
    });
    this.loadMain();
    this.getUsers(this.userPages);
  }

  loadMain(): void {
    this.countriesLoading = true;
    this.countriesError = '';

    this.api.getMain().subscribe({
      next: (res) => {
        this.countries = [...(res.data?.countries ?? [])]
          .map(country => ({
            ...country,
            code: country.code.trim().toUpperCase(),
            dialCode: country.dialCode?.trim() || null
          }))
          .filter(country => !country.status || country.status.toLowerCase() === 'active')
          .sort((left, right) => left.name.localeCompare(right.name));

        if (!this.countries.length) {
          this.countriesError = 'No countries are currently available.';
          return;
        }

        if (!this.selectedCountry) {
          this.country?.setValue(this.resolveDefaultCountryCode());
        }
      },
      error: (err) => {
        this.countriesLoading = false;
        this.countriesError =
          err?.error?.message ||
          err?.error?.Message ||
          err?.error?.Error?.Details ||
          'Unable to load the available countries.';
        this.notifyService.showError(this.countriesError, 'Error');
      },
      complete: () => {
        this.countriesLoading = false;
      },
    });
  }

  onAdminCountryChanged(): void {
    this.phone?.reset('');
  }

  onAdminPhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const country = this.selectedCountry;

    if (!country) return;

    if (this.phoneEntrySupported) {
      const digits = this.extractNationalDigits(input.value, country);
      const formatted = this.formatNationalDigits(
        digits,
        this.parsePhoneGroups(country.phoneNumberFormat)
      );

      this.phone?.setValue(formatted, { emitEvent: false });
      return;
    }

    const formatted = this.formatGenericPhone(input.value, country);
    this.phone?.setValue(formatted, { emitEvent: false });
  }

  countryDialCode(country: MiniCountryDto): string {
    return country.dialCode?.trim() || `(${country.code})`;
  }

  countryFlag(countryCode: string): string {
    const code = countryCode.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(code)) return '🌐';

    return String.fromCodePoint(
      ...Array.from(code).map(character => 127397 + character.charCodeAt(0))
    );
  }

  getUsers(page: number) {
    this.loading = true;
    this.api.getUsers(page).subscribe({
      next: (res) => {
        this.meta = res.meta;
        this.admins = res.data || [];
      },
      error: (err) => {
        this.notifyService.showError(err.error?.Error?.Details || 'Failed to load Users', "Error");
      },
      complete: () => (this.loading = false),
    });
  }

  addAdmin() {
    if (this.adminForm.invalid) {
      this.adminForm.markAllAsTouched();
      return;
    }

    if (this.phoneInvalid) {
      this.phone?.markAsTouched();
      this.notifyService.showError(
        'Enter a valid phone number for the selected country or leave it blank.',
        'Phone Number'
      );
      return;
    }

    const phone = this.buildInternationalPhone(String(this.phone?.value ?? ''));
    const newUser: CreateUser = {
      firstName: String(this.firstName?.value ?? '').trim(),
      lastName: String(this.lastName?.value ?? '').trim(),
      email: String(this.email?.value ?? '').trim().toLowerCase(),
      phone: phone ?? undefined,
      country: String(this.country?.value ?? '').trim().toUpperCase(),
      role: this.role?.value
    }
    
    this.api.addAdmin(newUser).subscribe({
      next: (res) => {
        this.ngOnInit();
        this.adminForm.reset({
          role: 'Admin',
          country: this.resolveDefaultCountryCode(),
          phone: ''
        });
        this.notifyService.showSuccess(`${newUser.role} staff user added successfully`, "Success")
      },
      error: (err) => {
        this.notifyService.showError(err.error?.Error?.Details || 'Failed to create staff user', "Error");
      }
    });
  }

  openEditModal(admin: UserDto) {
    this.editAdminForm.patchValue({
      id: admin.id,
      firstName: admin.firstName,
      lastName: admin.lastName,
      phone: admin.phone,
      country: admin.country,
      status: admin.status
    });

    const el = document.getElementById('editAdminModal');
    if (!el) return;

    this.editModal = Modal.getOrCreateInstance(el);
    this.editModal.show();
  }

  openResetOtpModal(admin: UserDto) {
    this.selectedUser = admin;
    const el = document.getElementById('openResetOtpModal');
    if (!el) return;

    this.tfaModal = Modal.getOrCreateInstance(el);
    this.tfaModal.show();
  }

  closeEditModal() {
    this.editModal?.hide();
  }

  saveAdminEdits() {
    if (this.editAdminForm.invalid) {
      this.editAdminForm.markAllAsTouched();
      return;
    }

    // call API -> update admin
    this.closeEditModal();
  }

  disableAdmin(userId: string) {
    const user = this.admins.find(c => c.id === userId);
    this.deletingId = user!.id;
    if (confirm(`Are you sure you want to delete ${user?.firstName} ${user?.lastName}? This action cannot be undone.`)) {
      this.api.deleteCurrency(user!.id).subscribe({
        next: () => {
          this.admins = this.admins.filter(c => c.id !== user!.id);
          this.notifyService.showSuccess("User Disabled", "Success");
        },
        error: (err) => {
          this.notifyService.showError(err.error?.Error?.Details || 'Failed to disable user', "Error");
        }
      });
    }
  }

  disable2fa(id: string) {
    this.disable2FaLoading = false;
    // call API -> disable admin
    const payload = {
      userId: id
    };

    this.authApi.resetOtp(payload).subscribe({
      next: (res) => {
        const index = this.admins.findIndex(a => a.id === id);
        if (index !== -1) {
          const admin = this.admins[index];

          this.admins[index] = {
            ...admin,
            otp: admin.otp ? { ...admin.otp, otpEnabled: false } : admin.otp
          };
        }

        this.notifyService.showSuccess('2FA Disabled for user successfully', "Success");
        this.close2faModal();
      },
      error: (err) => {
        this.notifyService.showError(err.error?.Error?.Details || 'Failed to disable 2FA', "Error");
      },
      complete: () => (this.disable2FaLoading = false),
    }); 
  }

  openUserModal(user: UserDto) {
    this.selectedUser = user;

    const el = document.getElementById('userDetailsModal');
    if (!el) return;

    this.api.getAdminUserWallets(user.id, this.userWalletPages).subscribe({
      next: (res) => {
        this.metaForWallet = res.meta
        this.selectedUserWallet = res.data || [];
      },
      error: (err) => {
        this.notifyService.showError(err.error?.Error?.Details || 'Failed to load user wallets', "Error");
      },
      complete: () => (this.loading = false),
    });

    this.api.getAdminUserOrders(user.id, this.userOrderPages).subscribe({
      next: (res) => {
        this.metaForOrder = res.meta
        this.selectedUserOrders = res.data || [];
      },
      error: (err) => {
        this.notifyService.showError(err.error?.Error?.Details || 'Failed to load user orders', "Error");
      },
      complete: () => (this.loading = false),
    });

    this.userModal = Modal.getOrCreateInstance(el);
    this.userModal.show();
  }

  closeUserModal() {
    this.userModal?.hide();
  }

  close2faModal() {
    this.tfaModal?.hide();
  }

  getStatusClass(status: string): string {
    return this.format.getUserStatusClass(status, true);
  }

  getOrderStatusClass(status: string): string {
    return this.format.getOrderStatusClass(status);
  }
  private buildInternationalPhone(value: string): string | null {
    const phone = value.trim();
    const country = this.selectedCountry;

    if (!phone || !country) return null;

    if (this.phoneEntrySupported && country.dialCode) {
      const digits = this.extractNationalDigits(phone, country);
      return digits ? `${country.dialCode}${digits}` : null;
    }

    const dialCode = country.dialCode?.trim() ?? '';

    if (dialCode) {
      const nationalDigits = this.extractGenericNationalDigits(phone, dialCode);
      return nationalDigits ? `${dialCode}${nationalDigits}` : null;
    }

    if (!phone.startsWith('+')) return null;

    const digits = phone.replace(/\D/g, '').slice(0, 15);
    return digits ? `+${digits}` : null;
  }

  private isValidGenericPhone(value: string, country: MiniCountryDto): boolean {
    const dialCode = country.dialCode?.trim() ?? '';

    if (dialCode) {
      const nationalDigits = this.extractGenericNationalDigits(value, dialCode);
      const totalDigits = dialCode.replace(/\D/g, '').length + nationalDigits.length;

      return nationalDigits.length >= 4 && totalDigits >= 7 && totalDigits <= 15;
    }

    if (!value.trim().startsWith('+')) return false;

    const digits = value.replace(/\D/g, '');
    return digits.length >= 7 && digits.length <= 15;
  }

  private extractGenericNationalDigits(value: string, dialCode: string): string {
    const trimmed = value.trim();
    let digits = trimmed.replace(/\D/g, '');
    const dialCodeDigits = dialCode.replace(/\D/g, '');

    if (
      trimmed.startsWith('+') &&
      dialCodeDigits &&
      digits.startsWith(dialCodeDigits)
    ) {
      digits = digits.slice(dialCodeDigits.length);
    }

    const maximumNationalDigits = Math.max(1, 15 - dialCodeDigits.length);
    return digits.slice(0, maximumNationalDigits);
  }

  private formatGenericPhone(value: string, country: MiniCountryDto): string {
    const dialCode = country.dialCode?.trim() ?? '';

    if (dialCode) {
      const digits = this.extractGenericNationalDigits(value, dialCode);
      return this.groupGenericDigits(digits);
    }

    const digits = value.replace(/\D/g, '').slice(0, 15);
    return digits ? `+${this.groupGenericDigits(digits)}` : '';
  }

  private groupGenericDigits(digits: string): string {
    if (!digits) return '';

    const parts: string[] = [];
    for (let index = 0; index < digits.length; index += 3) {
      parts.push(digits.slice(index, index + 3));
    }

    return parts.join(' ');
  }

  private extractNationalDigits(value: string, country: MiniCountryDto): string {
    let digits = value.replace(/\D/g, '');
    const dialCodeDigits = (country.dialCode ?? '').replace(/\D/g, '');
    const maximum = country.phoneNumberMaxLength ?? 15;

    if (dialCodeDigits && digits.startsWith(dialCodeDigits) && digits.length > maximum) {
      digits = digits.slice(dialCodeDigits.length);
    }

    if (country.removeNationalPrefixZero && digits.startsWith('0')) {
      digits = digits.slice(1);
    }

    return digits.slice(0, maximum);
  }

  private parsePhoneGroups(value?: string | null): number[] {
    return (value ?? '')
      .split(',')
      .map(group => Number(group.trim()))
      .filter(group => Number.isInteger(group) && group > 0);
  }

  private formatNationalDigits(digits: string, groups: number[]): string {
    if (!groups.length) return digits;

    const parts: string[] = [];
    let cursor = 0;

    for (const groupLength of groups) {
      if (cursor >= digits.length) break;
      parts.push(digits.slice(cursor, cursor + groupLength));
      cursor += groupLength;
    }

    if (cursor < digits.length) {
      parts.push(digits.slice(cursor));
    }

    return parts.join(' ');
  }

  private resolveDefaultCountryCode(): string {
    return (
      this.countries.find(country => country.code === this.browserCountryCode()) ??
      this.countries.find(country => country.code === 'NG') ??
      this.countries[0]
    )?.code ?? '';
  }

  private browserCountryCode(): string {
    const locale = navigator.language || '';
    const segments = locale.split('-');
    return segments.length > 1 ? segments[segments.length - 1].toUpperCase() : '';
  }

}
